import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { IBuilding } from 'src/app/building-managment/building-management-interfaces';
import { IDevice } from 'src/app/building-managment/building-unit-details/add-lock/lock-management-interfaces';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { getFullName } from 'src/app/shared/utils/full-name.util';
import { convertParseDateTime } from 'src/app/shared/utils/utility.service';
import {
  AddUserAccessRequest,
  GrantUserAccessRequest,
  GrantUserAccessResult,
  hasRequiredContact,
  profileEmail,
  PopupProfile,
  ProfileManagementService,
  ProfileUpdatedEvent,
  profilePopupErrorKey,
} from '../profile-management.service';
import {
  AssignFloor,
  AssignUnit,
} from '../profile-assign-unit/profile-assign-unit.component';
import {
  ASSIGN_STEP,
  ASSIGN_USER_TYPE_OPTIONS,
  isContactRequiredUserType,
  isSitewideUserType,
} from '../profile-assign-user-type/profile-assign-user-type.component';
import {
  buildReviewUnits,
  ReviewUnit,
} from '../profile-review-access/profile-review-access.component';

export interface AssignAccessTime {
  startTime?: string;
  expireTime?: string;
  repeat?: boolean;
  repeatStartTime?: string;
  repeatEndTime?: string;
  repeatDays?: string[];
}

const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
// Defaults applied by the existing access time flow when repeat times are left empty.
const DEFAULT_REPEAT_START = '12:00 AM';
const DEFAULT_REPEAT_END = '11:59 PM';
const DISPLAY_DATE_FORMAT = "EEE, MMM do, yyyy 'at' h:mm a";
const API_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

@Component({
  selector: 'app-profile-confirm-access',
  templateUrl: './profile-confirm-access.component.html',
  styleUrls: ['./profile-confirm-access.component.scss'],
})
export class ProfileConfirmAccessComponent implements OnChanges, OnDestroy {
  @Input() profileDetails!: PopupProfile;
  @Input() siteId!: string;
  @Input() userType?: number;
  @Input() building?: IBuilding;
  @Input() floors: AssignFloor[] = [];
  @Input() selectedUnits: AssignUnit[] = [];
  @Input() reviewMode: 'units' | 'locks' = 'units';
  @Input() selectedLocks: IDevice[] = [];
  @Input() accessTime?: AssignAccessTime;
  @Input() timeZone = '';
  @Input() siteName = '';
  @Input() dialogRef?: { close: () => void };
  @Input() stepper = 24;
  @Output() stepperChange = new EventEmitter<number>();
  @Output() editProfileRequested = new EventEmitter<void>();
  @Output() accessGranted = new EventEmitter<GrantUserAccessResult>();
  @Output() lockAccessAdded = new EventEmitter<void>();
  @Output() profileUpdated = new EventEmitter<ProfileUpdatedEvent>();
  units: ReviewUnit[] = [];
  expandedUnitIds = new Set<string>();
  isSubmitting = false;
  isSendingInvite = false;
  accessGrantSucceeded = false;
  // Retained so an invitation retry never re-grants (backend: RESOURCE_ALREADY_SHARED).
  grantResult?: GrantUserAccessResult;
  inviteErrorKey = '';
  private destroyed = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private service: ProfileManagementService,
    private notificationService: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.floors || changes.selectedUnits) {
      this.units = buildReviewUnits(this.floors, this.selectedUnits);
    }
  }

  get userName(): string {
    const p = this.profileDetails;
    return getFullName(p?.firstName, p?.middleName, p?.lastName);
  }

  get email(): string {
    return profileEmail(this.profileDetails);
  }

  get phone(): string {
    const raw = String(this.profileDetails?.phoneNumber || '').trim();
    const digits = raw.replace(/\D/g, '');
    const national =
      digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return national.length === 10
      ? `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
      : raw;
  }

  get userTypeKey(): string {
    return (
      ASSIGN_USER_TYPE_OPTIONS.find((option) => option.value === this.userType)
        ?.labelKey || ''
    );
  }

  get sitewide(): boolean {
    return isSitewideUserType(this.userType);
  }

  get contactRequired(): boolean {
    return isContactRequiredUserType(this.userType);
  }

  get canConfirm(): boolean {
    if (this.isSubmitting || this.isSendingInvite) return false;
    if (this.reviewMode === 'locks') {
      return (
        !this.accessGrantSucceeded &&
        !!this.siteId &&
        !!this.profileDetails?.email &&
        !!this.building &&
        this.selectedUnits.length === 1 &&
        this.selectedLocks.length > 0 &&
        this.selectedLocks.every((lock) => !!lock.deviceId)
      );
    }
    if (this.accessGrantSucceeded) return this.canRetryInvite;
    return this.sitewide || this.units.length > 0;
  }

  get canRetryInvite(): boolean {
    return this.contactRequired && this.grantResult?.canSendInvitation === true;
  }

  // After a required-contact grant only the invitation (or close) may follow.
  get navigationLocked(): boolean {
    return (
      this.isSubmitting || (this.contactRequired && this.accessGrantSucceeded)
    );
  }

  get timeSummary(): string {
    const time = this.accessTime || {};
    if (time.repeat) {
      return `${time.repeatStartTime || DEFAULT_REPEAT_START} - ${
        time.repeatEndTime || DEFAULT_REPEAT_END
      }`;
    }
    const start = time.startTime || this.translate.instant('ACCESS_TIME_NOW');
    const end = time.expireTime || this.translate.instant('NEVER');
    return `${start} - ${end}`;
  }

  get daysSummary(): string {
    const time = this.accessTime;
    if (!time?.repeat || !time.repeatDays?.length) return '';
    const format = new Intl.DateTimeFormat(
      this.translate.currentLang || undefined,
      { weekday: 'short', timeZone: 'UTC' }
    );
    return (
      WEEK_DAYS.map((day, index) => ({ day, index }))
        .filter(({ day }) => time.repeatDays!.includes(day))
        // 2026-01-04 is a Sunday, so index offsets map to WEEK_DAYS order.
        .map(({ index }) =>
          format.format(new Date(Date.UTC(2026, 0, 4 + index)))
        )
        .join(', ')
    );
  }

  get lockLocation(): string {
    const unit = this.selectedUnits[0];
    const floor = this.floors.find((item) => item.floorId === unit?.floorId);
    return [floor?.floorName, this.building?.buildingName]
      .filter(Boolean)
      .join(', ');
  }

  isExpanded(unit: ReviewUnit): boolean {
    return this.expandedUnitIds.has(unit.unitId);
  }

  toggleUnit(unit: ReviewUnit): void {
    if (this.isExpanded(unit)) this.expandedUnitIds.delete(unit.unitId);
    else this.expandedUnitIds.add(unit.unitId);
  }

  viewAll(): void {
    this.expandedUnitIds = new Set(this.units.map((unit) => unit.unitId));
  }

  editProfile(): void {
    if (this.navigationLocked) return;
    this.editProfileRequested.emit();
    this.stepperChange.emit(
      this.contactRequired
        ? ASSIGN_STEP.completeUserInfo
        : ASSIGN_STEP.editInformation
    );
  }

  confirm(): void {
    if (!this.canConfirm) return;
    if (this.reviewMode === 'locks') {
      this.addLockAccess();
      return;
    }
    if (this.accessGrantSucceeded) {
      this.sendInvite();
      return;
    }
    if (this.contactRequired && !hasRequiredContact(this.profileDetails)) {
      this.editProfile();
      return;
    }
    const email = this.profileDetails?.email;
    if (!email || !this.siteId || !this.userType) return;
    if (!this.sitewide && (!this.building || !this.selectedUnits?.length))
      return;
    this.isSubmitting = true;
    this.service
      .grantUserAccess(this.buildRequest(email))
      .pipe(
        takeUntil(this.destroyed),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: (result) => {
          this.accessGrantSucceeded = true;
          this.grantResult = result;
          this.preserveIdentity(email, result.email, result.stagedEmail);
          this.accessGranted.emit(result);
          if (!result.canSendInvitation || !this.contactRequired) {
            this.stepperChange.emit(ASSIGN_STEP.accessAssigned);
          } else {
            this.sendInvite();
          }
        },
        // Backend errors carry an errorType and an already-localized message.
        error: (error: any) =>
          this.notificationService.showError(
            error?.errorType
              ? error.message
              : this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR')
          ),
      });
  }

  private addLockAccess(): void {
    this.isSubmitting = true;
    this.service
      .addUserAccess(this.buildLockRequest())
      .pipe(
        takeUntil(this.destroyed),
        finalize(() => (this.isSubmitting = false))
      )
      .subscribe({
        next: () => {
          this.accessGrantSucceeded = true;
          this.lockAccessAdded.emit();
        },
        error: (error: any) =>
          this.notificationService.showError(
            error?.errorType
              ? error.message
              : this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR')
          ),
      });
  }

  buildLockRequest(): AddUserAccessRequest {
    const unit = this.selectedUnits[0];
    return this.applyAccessTime<AddUserAccessRequest>({
      siteId: this.siteId,
      targetEmail: this.profileDetails.email!,
      entities: this.selectedLocks.map((lock) => ({
        buildingId: this.building!.buildingId,
        floorId: unit.floorId,
        unitId: unit.unitId,
        deviceId: lock.deviceId!,
      })),
    });
  }

  // Only ever reached with a retained canSendInvitation === true grant result.
  private sendInvite(): void {
    const grant = this.grantResult;
    if (this.isSendingInvite || grant?.canSendInvitation !== true) return;
    // Use the saved phone verbatim: backend rejects a phone different from the stored value.
    const phone = grant.phoneNumber || this.profileDetails.phoneNumber;
    if (!phone) {
      this.inviteErrorKey = 'PROFILE_POPUP_MOBILE_INVITE_UNAVAILABLE';
      return;
    }
    this.inviteErrorKey = '';
    this.isSendingInvite = true;
    this.service
      .mobileAccessInvite(this.siteId, grant.email, phone)
      .pipe(
        takeUntil(this.destroyed),
        finalize(() => (this.isSendingInvite = false))
      )
      .subscribe({
        next: (result) => {
          if (result.phoneNumber)
            this.profileDetails.phoneNumber = result.phoneNumber;
          this.profileDetails.invitationPending = true;
          this.preserveIdentity(
            grant.email,
            result.email,
            result.stagedEmail,
            true
          );
          this.stepperChange.emit(ASSIGN_STEP.inviteSent);
        },
        error: (error) =>
          (this.inviteErrorKey = profilePopupErrorKey(
            error,
            'PROFILE_POPUP_INVITE_FAILED'
          )),
      });
  }

  private preserveIdentity(
    previousEmail: string,
    email: string,
    stagedEmail?: string | null,
    force = false
  ): void {
    if (!email) return;
    const savedStagedEmail = stagedEmail ?? null;
    const contactChanged = this.profileDetails.stagedEmail !== savedStagedEmail;
    this.profileDetails.email = email;
    this.profileDetails.stagedEmail = savedStagedEmail;
    if (!force && previousEmail === email && !contactChanged) return;
    this.profileUpdated.emit({
      previousEmail,
      email,
      profileDetails: this.profileDetails,
    });
  }

  // Same time conversion as the add-user share access flow.
  buildRequest(email: string): GrantUserAccessRequest {
    const request: GrantUserAccessRequest = {
      siteId: this.siteId,
      email,
      userType: this.userType!,
      entities: this.sitewide
        ? []
        : this.selectedUnits.map((unit) => ({
            buildingId: this.building!.buildingId,
            floorId: unit.floorId,
            unitId: unit.unitId,
          })),
    };
    if (this.sitewide) {
      request.startTime = CONSTANTS.NOW;
      return request;
    }
    return this.applyAccessTime(request);
  }

  private applyAccessTime<T extends AssignAccessTime>(request: T): T {
    const time = this.accessTime || {};
    if (time.repeat) {
      const hasTimes = !!(time.repeatStartTime && time.repeatEndTime);
      request.repeatStartTime = convertParseDateTime(
        hasTimes ? time.repeatStartTime : DEFAULT_REPEAT_START,
        'hh:mm a',
        'HH:mm'
      );
      request.repeatEndTime = convertParseDateTime(
        hasTimes ? time.repeatEndTime : DEFAULT_REPEAT_END,
        'hh:mm a',
        'HH:mm'
      );
      request.repeatDays = time.repeatDays;
    } else {
      request.startTime = time.startTime
        ? convertParseDateTime(
            time.startTime,
            DISPLAY_DATE_FORMAT,
            API_DATE_FORMAT
          )
        : CONSTANTS.NOW;
      if (time.expireTime) {
        request.expireTime = convertParseDateTime(
          time.expireTime,
          DISPLAY_DATE_FORMAT,
          API_DATE_FORMAT
        );
      }
    }
    return request;
  }

  back(): void {
    if (this.navigationLocked) return;
    this.stepperChange.emit(
      this.reviewMode === 'units' && this.sitewide
        ? ASSIGN_STEP.userType
        : ASSIGN_STEP.timeEnd
    );
  }

  close(): void {
    if (!this.isSubmitting) this.dialogRef?.close();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
